import { useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AppShell from './app/AppShell.jsx';
import { SessionProvider, useSession } from './app/session.jsx';
import { WalletProvider } from './app/wallet.jsx';
import { PrintProvider } from './features/print/PrintStore.jsx';
import HomePage from './pages/HomePage.jsx';
import LibraryPage from './pages/LibraryPage.jsx';
import PairPage from './pages/PairPage.jsx';
import ConnectPage from './pages/ConnectPage.jsx';
import PrintPage from './pages/PrintPage.jsx';
import DesignPage from './pages/DesignPage.jsx';
import BrowserPage from './pages/BrowserPage.jsx';
import AiPosterPage from './pages/AiPosterPage.jsx';
import { BackgroundGenerationProvider } from './contexts/BackgroundGenerationContext.jsx';
import { OrgQueueProvider } from './contexts/OrgQueueContext.jsx';
import { TourProvider } from './contexts/TourContext.jsx';
import { ConfirmProvider } from './contexts/ConfirmContext.jsx';
import { savedLanguage } from './i18n/index.js';
import LanguageSelectScreen from './components/LanguageSelectScreen.jsx';
import PrintablesPage from './pages/PrintablesPage.jsx';
import ResourcesPage from './pages/ResourcesPage.jsx';
import SchoolPage from './pages/SchoolPage.jsx';
import ScanPage from './pages/ScanPage.jsx';
import SettingsPage from './pages/SettingsPage.jsx';
import SignInPage from './pages/SignInPage.jsx';
import AuthCallbackPage from './pages/AuthCallbackPage.jsx';
import OnboardingPage from './pages/OnboardingPage.jsx';
import ToolPage from './pages/ToolPage.jsx';
import UiKit from './pages/UiKit.jsx';
import { Spinner } from './ui/index.jsx';
import './ui/base.css';

/** Every app page needs a Brightivity sign-in; signed-out visitors go straight to /sign-in (owner: no landing page). */
function RequireAuth({ children }) {
  const { signedIn, checked, session } = useSession();
  const location = useLocation();
  if (!signedIn) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  if (!checked) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner /></div>;
  // Profile not finished (desktop OnboardingModal after sign-in) → the wizard first.
  if (session && session.onboardingCompleted === false) return <Navigate to="/onboarding" replace />;
  return <AppShell>{children}</AppShell>;
}

/** Signed in, outside the frame (the profile wizard). */
function RequireSignedIn({ children }) {
  const { signedIn, checked } = useSession();
  if (!signedIn) return <Navigate to="/sign-in" replace />;
  if (!checked) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner /></div>;
  return children;
}

const tool = (page) => <RequireAuth><ToolPage page={page} /></RequireAuth>;

export default function App() {
  // Desktop first-run gate: no language chosen in this browser yet → pick one before anything else (also before sign-in).
  const [languageChosen, setLanguageChosen] = useState(() => Boolean(savedLanguage()));
  if (!languageChosen) return <LanguageSelectScreen onDone={() => setLanguageChosen(true)} />;
  return (
    <SessionProvider>
      <WalletProvider>
      <OrgQueueProvider>
      <TourProvider>
      <PrintProvider>
      <ConfirmProvider>
      <BackgroundGenerationProvider>
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignInPage />} />
        <Route path="/license" element={<SignInPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/onboarding" element={<RequireSignedIn><OnboardingPage /></RequireSignedIn>} />
        <Route path="/__ui" element={<UiKit />} />
        <Route path="/" element={<RequireAuth><HomePage /></RequireAuth>} />
        <Route path="/print" element={<RequireAuth><PrintPage /></RequireAuth>} />
        <Route path="/scan" element={<RequireAuth><ScanPage /></RequireAuth>} />
        <Route path="/design" element={<RequireAuth><DesignPage /></RequireAuth>} />
        <Route path="/design/ai" element={<RequireAuth><AiPosterPage /></RequireAuth>} />
        <Route path="/browser" element={<RequireAuth><BrowserPage /></RequireAuth>} />
        <Route path="/library" element={<RequireAuth><LibraryPage /></RequireAuth>} />
        <Route path="/printables" element={<RequireAuth><PrintablesPage /></RequireAuth>} />
        <Route path="/resources" element={<RequireAuth><ResourcesPage /></RequireAuth>} />
        <Route path="/school" element={<RequireAuth><SchoolPage /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
        <Route path="/pair" element={<RequireAuth><PairPage /></RequireAuth>} />
        <Route path="/connect" element={<RequireAuth><ConnectPage /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </BackgroundGenerationProvider>
      </ConfirmProvider>
      </PrintProvider>
      </TourProvider>
      </OrgQueueProvider>
      </WalletProvider>
    </SessionProvider>
  );
}
